import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Drawer,
  Divider,
  List,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import MenuItem from "./MenuItem";

import useSecurity from "../../hooks/useSecurity";
import useData from "../../hooks/useData";

const PageMenu = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { logout } = useSecurity();
  const { executeAction, todaysPasscode } = useData();
  const executeActionRef = useRef(executeAction);
  const [menuPages, setMenuPages] = useState([]);

  const currentUserId = useSelector((state) => state.authentication.userId);
  const email = useSelector((state) => state.authentication.email);
  const role = useSelector((state) => state.authentication.role);
  const userId = useSelector((state) => state.authentication.userId);
  const pageSecurityRole = role === "teamLead" ? "supervisor" : role;
  const isRep = role === "rep";

  useEffect(() => {
    executeActionRef.current = executeAction;
  }, [executeAction]);

  useEffect(() => {
    let isMounted = true;

    const loadMenu = async () => {
      const result = await executeActionRef.current({
        entityName: "PageSecurity",
        action: "getMenu",
        data: { role: pageSecurityRole, userId },
      });

      if (!isMounted) {
        return;
      }

      if (result?.isSuccessful && Array.isArray(result.data)) {
        setMenuPages(result.data);
      }
    };

    if (open && role) {
      loadMenu();
    }

    return () => {
      isMounted = false;
    };
  }, [open, role, pageSecurityRole, userId]);

  const menuGroups = useMemo(() => {
    const grouped = {};

    menuPages.forEach((page) => {
      const group = page.menuGroup || "Core";
      if (!grouped[group]) {
        grouped[group] = [];
      }

      grouped[group].push(page);
    });

    Object.keys(grouped).forEach((group) => {
      grouped[group].sort((a, b) => {
        const aOrder = Number(a.menuOrder || 0);
        const bOrder = Number(b.menuOrder || 0);
        return aOrder - bOrder;
      });
    });

    return Object.entries(grouped).sort(([, groupA], [, groupB]) => {
      const aOrder = Number(groupA?.[0]?.menuOrder || 0);
      const bOrder = Number(groupB?.[0]?.menuOrder || 0);
      return aOrder - bOrder;
    });
  }, [menuPages]);

  const logoutHandler = () => {
    logout();
  };

  const handleNavigation = (page) => {
    if (!page?.routePath) {
      return;
    }

    onClose();
    const state =
      page.initialParams && typeof page.initialParams === "object"
        ? page.initialParams
        : {};

    navigate(page.routePath, { state });
  };

  const homePage = menuPages.find((page) => page.routePath === "/home");
  const corePages =
    menuGroups.find(([groupName]) => groupName === "Core")?.[1] || [];
  const nonCoreGroups = menuGroups.filter(
    ([groupName]) => groupName !== "Core",
  );

  if (role === "client") {
    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
      >
        <Box
          backgroundColor="#ffffff"
          width="400px"
        >
          <List>
            <MenuItem
              name="logout"
              iconName="Logout"
              onClick={() => logoutHandler()}
            />
          </List>
        </Box>
      </Drawer>
    );
  }

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
      >
        <Box
          backgroundColor="#ffffff"
          width="400px"
        >
          <List>
            {homePage ? (
              <MenuItem
                name={homePage.labelKey}
                iconName={homePage.iconName || "Home"}
                onClick={() => handleNavigation(homePage)}
              />
            ) : (
              <MenuItem
                name="home"
                iconName="Home"
                onClick={() => navigate("/home")}
              />
            )}

            <Divider sx={{ my: 1 }} />

            {corePages
              .filter((page) => page.routePath !== "/home")
              .map((page) => (
                <MenuItem
                  key={page.key}
                  name={page.labelKey}
                  iconName={page.iconName || "Article"}
                  onClick={() => handleNavigation(page)}
                />
              ))}

            {nonCoreGroups.map(([groupName, pages]) => (
              <Accordion key={groupName}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600}>{groupName}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {pages.map((page) => (
                    <MenuItem
                      key={page.key}
                      name={page.labelKey}
                      iconName={page.iconName || "Article"}
                      onClick={() => handleNavigation(page)}
                    />
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}

            {menuPages.length === 0 && (
              <Typography sx={{ px: 2, py: 1.5 }}>
                No pages available for this role.
              </Typography>
            )}

            <>
              <Divider sx={{ my: 1 }} />
              <MenuItem
                name="logout"
                iconName="Logout"
                onClick={() => logoutHandler()}
              />
              <Divider />
              {!isRep && (
                <Box sx={{ padding: "8px" }}>
                  <Typography sx={{ fontSize: "24px", fontWeight: "bold" }}>
                    Passcode: {todaysPasscode()}
                  </Typography>
                  <Typography>{`userId : ${currentUserId}`}</Typography>
                  <Typography>{`email : ${email}`}</Typography>
                </Box>
              )}
            </>
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default PageMenu;
